import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'stores/store';
import { getCookie, parseCookieValue } from 'utils/cookies';
import { isBrowser } from 'utils/domUtils';
import { ViewModeCookieName } from 'utils/globalTypes';
import { useMobileDetect } from 'utils/hooks/useMobileDetect';

interface IPageView {
  cookieName: ViewModeCookieName;
  defaultView?: string;
}

const usePageView = ({ cookieName, defaultView }: IPageView) => {
  const store = useStore();
  const { isMobile, isTablet } = useMobileDetect();

  const isDesktop = useMemo(() => !isMobile && !isTablet, [isMobile, isTablet]);

  const SSRCookie = useMemo(
    () => parseCookieValue(decodeURI(store.cookie?.value as string)),
    [store.cookie?.value]
  );
  const viewCookie = useMemo(() => isBrowser() && getCookie(cookieName), [cookieName]);

  const [pageView, setPageView] = useState({
    tableView: SSRCookie?.assetBrowsingTableView || viewCookie?.assetBrowsingTableView,
    mapView: SSRCookie?.assetBrowsingMapView || viewCookie?.assetBrowsingMapView
  });

  const onShowMap = useCallback(() => {
    store.AssetStore.setAssetBrowsingMapView(true, cookieName);
    store.AssetStore.setAssetBrowsingTableView(false, cookieName);
  }, [store.AssetStore, cookieName]);

  const onCloseMap = useCallback(() => {
    store.AssetStore.setAssetBrowsingMapView(false, cookieName);
  }, [store.AssetStore, cookieName]);

  useEffect(() => {
    store.AssetStore.initAssetBrowsingView(defaultView, cookieName);
  }, [store.AssetStore, defaultView, cookieName]);

  const storeTableView = store.AssetStore[cookieName].assetBrowsingTableView;
  const storeMapView = store.AssetStore[cookieName].assetBrowsingMapView;

  useEffect(() => {
    setPageView({
      tableView: isDesktop && store.AssetStore[cookieName].assetBrowsingTableView,
      mapView:
        !store.AssetStore[cookieName].assetBrowsingTableView &&
        store.AssetStore[cookieName].assetBrowsingMapView
    });
  }, [cookieName, store.AssetStore, storeTableView, storeMapView, isDesktop]);

  return { ...pageView, onShowMap, onCloseMap };
};

export default usePageView;
